import React, { useEffect, useMemo, useState } from "react";
import { useApiCall } from "../custom-hooks/api-call";

/* =========================================================================
   Legendesk Sales — Overview (bird's-eye company dashboard)
   ========================================================================= */

// ---- palette -------------------------------------------------------------
const C = {
  linen: "#F7ECDF", linenDeep: "#EFE1CF", ink: "#0A0C12", ink70: "#3A3D47",
  ink50: "#6B6E78", ink30: "#9A9CA4", card: "#FFFFFF", line: "#E7DAC6",
  lineSoft: "#F0E6D6", indigo: "#4F6EF7", indigoSoft: "#EAEEFF",
  green: "#1F9D6B", greenSoft: "#E4F5EC", amber: "#C9871A", amberSoft: "#FBEFD6",
  red: "#D0503C", redSoft: "#FBE6E0",
};
const FONT_DISP = "'Sora', system-ui, sans-serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

// ---- rng -----------------------------------------------------------------
function mulberry32(a){return function(){a|=0;a=(a+0x6d2b79f5)|0;let t=Math.imul(a^(a>>>15),1|a);t=(t+Math.imul(t^(t>>>7),61|t))^t;return((t^(t>>>14))>>>0)/4294967296;};}
function hash(s){let h=2166136261;for(let i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619);}return h>>>0;}
function gauss(r){return (r()+r()+r())/3;}

const FIRST=["Ayesha","Bilal","Zara","Hamza","Sana","Usman","Mariam","Faisal","Noor","Ahmed","Hira","Talha","Iqra","Danish","Fatima","Saad","Amna","Rehan","Kiran","Owais","Mehak","Zain","Rida","Adeel","Anaya","Kashif","Laiba","Waleed","Sadia","Umar","Aiman","Junaid","Sania","Haris","Areeba","Fahad","Nimra","Shahzad","Warda","Bilawal","Emma","Liam","Sophia","Noah","Olivia","Ethan","Mia","Lucas","Ella","Mason"];
const LAST=["Khan","Malik","Sheikh","Raza","Qureshi","Butt","Ansari","Hashmi","Siddiqui","Farooq","Chaudhry","Baig","Awan","Mirza","Rana","Shah","Gill","Dar","Jamil","Nawaz","Rivera","Chen","Patel","Nguyen","Brooks","Silva"];
const TEAMS=["Apex","Titan","Falcon","Orbit","Vanguard"];

function buildAgents(count=100){
  const rng=mulberry32(20260703);const out=[];const used=new Set();
  for(let i=0;i<count;i++){
    let name;do{name=FIRST[Math.floor(rng()*FIRST.length)]+" "+LAST[Math.floor(rng()*LAST.length)];}while(used.has(name));used.add(name);
    const skill=0.55+gauss(rng)*0.85;const vol=0.05+rng()*0.22;
    const baseTarget=9000+Math.round(rng()*17000);
    const refundBase=rng()<0.16?0.14+rng()*0.16:0.02+rng()*0.09;
    const history=[];let drift=(rng()-0.5)*0.06;
    for(let p=0;p<6;p++)history.push(Math.max(0.15,skill+drift*(p-2.5)+(gauss(rng)-0.5)*vol*2));
    out.push({id:"AG"+String(i+1).padStart(3,"0"),name,team:TEAMS[i%TEAMS.length],skill,vol,baseTarget,refundBase,history});
  }
  return out;
}
const AGENTS=buildAgents(100);

const PERIODS=[
  // {key:"today",label:"Today",factor:1/22},
  // {key:"yesterday",label:"Yesterday",factor:1/22},
  // {key:"this_week",label:"This week",factor:1/4.3},
  {key:"this_month",label:"This month",factor:1},
  // {key:"last_30",label:"Last 30 days",factor:1},
  {key:"last_month",label:"Last month",factor:0.96},
  {key:"rolling_q",label:"Rolling quarter",factor:3},
  {key:"this_q",label:"This quarter",factor:2.1},
  {key:"last_q",label:"Last quarter",factor:3},
  {key:"this_year",label:"This year",factor:6.4},
  {key:"all_time",label:"All time",factor:27},
];

function computeRow(agent,period){
  const rng=mulberry32(hash(agent.id+"|"+period.key));
  const attain=Math.max(0.12,agent.skill+(gauss(rng)-0.5)*agent.vol*2.2);
  const target=Math.round(agent.baseTarget*period.factor);
  const gross=Math.round(target*attain);
  const refundRatio=Math.max(0.005,agent.refundBase+(gauss(rng)-0.5)*0.05);
  const refunds=Math.round(gross*refundRatio);const net=gross-refunds;
  return {...agent,target,gross,net,refunds,refundRatio,progress:target>0?net/target:0};
}
function statusOf(p){if(p>=1)return"on_target";if(p>=0.9)return"on_track";if(p>=0.7)return"at_risk";return"behind";}
const STATUS_META={
  on_target:{label:"On target",c:C.green,bg:C.greenSoft},
  on_track:{label:"On track",c:C.indigo,bg:C.indigoSoft},
  at_risk:{label:"At risk",c:C.amber,bg:C.amberSoft},
  behind:{label:"Behind",c:C.red,bg:C.redSoft},
  unassigned:{ label: "Unassigned", c: C.gray, bg: C.graySoft}
};
function trendOf(h){const avg=h.reduce((a,b)=>a+b,0)/h.length;const std=Math.sqrt(h.reduce((a,b)=>a+(b-avg)**2,0)/h.length);const s=h[h.length-1]-h[0];if(std<0.12&&avg>=0.95)return"consistent";if(s>0.18)return"improving";if(s<-0.18)return"declining";if(std>=0.22)return"volatile";return"steady";}

function money(n){const a=Math.abs(n);const s=n<0?"-":"";if(a>=1e6)return s+"$"+(a/1e6).toFixed(a>=1e7?1:2)+"M";if(a>=1e3)return s+"$"+(a/1e3).toFixed(a>=1e4?0:1)+"K";return s+"$"+Math.round(a);}
const pct=(x)=>Math.round(x*100)+"%";
const initials=(n)=>{
  const p=n?.split(" ");
  return(p ? p[0][0]+(p[1]?p[1][0]:"") : "").toUpperCase();
};

// ---- product mix palette (assigned by rank, since products are dynamic) --
const PRODUCT_PALETTE=[C.indigo,C.green,C.amber,C.red,"#8B5CF6","#0EA5E9","#EC4899","#14B8A6","#F97316","#64748B"];

// ---- shared components ---------------------------------------------------
function Pill({label,color,bg}){
  return(<span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 9px",borderRadius:99,background:bg,color,fontSize:11.5,fontWeight:600}}><span style={{width:6,height:6,borderRadius:99,background:color}}/>{label}</span>);
}
function StatTile({label,value,sub,accent}){
  return(
    <div style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:14,padding:"12px 16px 12px 18px",flex:"1 1 150px",minWidth:140,position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",left:0,top:0,bottom:0,width:3,background:accent||C.indigo}}/>
      <div style={{fontSize:10.5,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{label}</div>
      <div style={{fontFamily:FONT_DISP,fontSize:19,fontWeight:700,marginTop:4,color:C.ink}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:C.ink50,marginTop:2}}>{sub}</div>}
    </div>
  );
}
function ProgressRing({value,color,size=132,stroke=12}){
  const capped=Math.min(Math.max(value,0),1);
  const r=(size-stroke)/2;
  const circ=2*Math.PI*r;
  const offset=circ*(1-capped);
  return(
    <div style={{position:"relative",width:size,height:size}}>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.lineSoft} strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{transition:"stroke-dashoffset .4s ease"}}/>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
        <div style={{fontFamily:FONT_DISP,fontWeight:700,fontSize:22,color:C.ink}}>{pct(value)}</div>
        <div style={{fontSize:10,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>of target</div>
      </div>
    </div>
  );
}
function TrendChart({categories,series,height=240,yFormatter=money}){
  const [gradId]=useState(()=>"tcg"+Math.random().toString(36).slice(2,9));
  const width=680,padL=58,padR=18,padT=18,padB=30;
  const innerW=width-padL-padR,innerH=height-padT-padB;
  const allVals=series.flatMap(s=>s.values);
  const maxV=Math.max(...allVals,0.0001);
  const minV=Math.min(0,...allVals);
  const span=(maxV-minV)||1;
  const xFor=(i)=>categories.length>1?padL+(i/(categories.length-1))*innerW:padL+innerW/2;
  const yFor=(v)=>padT+innerH-((v-minV)/span)*innerH;
  const ticks=4;
  const tickVals=Array.from({length:ticks+1},(_,i)=>minV+(span*i/ticks));
  const showArea=series.length===1;
  const dense=categories.length>12;
  const labelStep=dense?Math.ceil(categories.length/9):1;
  return(
    <div>
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        {series.map(s=>(
          <span key={s.label} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,color:C.ink70,fontWeight:600,background:C.linenDeep,padding:"4px 10px",borderRadius:99}}>
            <span style={{width:8,height:8,borderRadius:s.dashed?2:99,background:s.color,display:"inline-block",flexShrink:0}}/>{s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{width:"100%",height:"auto",display:"block"}}>
        {showArea&&(
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={series[0].color} stopOpacity="0.22"/>
              <stop offset="100%" stopColor={series[0].color} stopOpacity="0"/>
            </linearGradient>
          </defs>
        )}
        {tickVals.map((t,i)=>(
          <g key={i}>
            <line x1={padL} x2={width-padR} y1={yFor(t)} y2={yFor(t)} stroke={C.lineSoft} strokeWidth="1" strokeDasharray="2 4"/>
            <text x={padL-8} y={yFor(t)+4} textAnchor="end" fontSize="10.5" fontFamily={FONT_MONO} fill={C.ink30}>{yFormatter(t)}</text>
          </g>
        ))}
        {categories.map((c,i)=>(i%labelStep===0||i===categories.length-1)&&(
          <text key={c+i} x={xFor(i)} y={height-8} textAnchor="middle" fontSize="10.5" fontFamily={FONT_BODY} fill={C.ink50}>{c}</text>
        ))}
        {showArea&&(()=>{
          const s=series[0];
          const line=s.values.map((v,i)=>(i?"L":"M")+xFor(i).toFixed(1)+" "+yFor(v).toFixed(1)).join(" ");
          const areaD=`${line} L ${xFor(s.values.length-1).toFixed(1)} ${yFor(minV).toFixed(1)} L ${xFor(0).toFixed(1)} ${yFor(minV).toFixed(1)} Z`;
          return <path d={areaD} fill={`url(#${gradId})`} stroke="none"/>;
        })()}
        {series.map(s=>{
          const d=s.values.map((v,i)=>(i?"L":"M")+xFor(i).toFixed(1)+" "+yFor(v).toFixed(1)).join(" ");
          return(
            <g key={s.label}>
              <path d={d} fill="none" stroke={s.color} strokeWidth={s.dashed?1.8:2.4} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={s.dashed?"6 5":undefined}/>
              {s.values.map((v,i)=>(!dense||i%labelStep===0||i===s.values.length-1)&&(
                <circle key={i} cx={xFor(i)} cy={yFor(v)} r={s.dashed?"2.6":"3.6"} fill={C.card} stroke={s.color} strokeWidth={s.dashed?"1.8":"2.2"}>
                  <title>{`${categories[i]}: ${yFormatter(v)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---- OVERVIEW VIEW (bird's-eye, no filters — just glance and go) ----------
function DonutChart({slices,size=140,stroke=22}){
  const total=slices.reduce((s,x)=>s+x.value,0)||1;
  const r=(size-stroke)/2;
  const circ=2*Math.PI*r;
  let acc=0;
  return(
    <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.lineSoft} strokeWidth={stroke}/>
      {slices.map((s,i)=>{
        const frac=s.value/total;
        const dash=frac*circ-1.5; // tiny gap between slices
        const offset=-acc*circ;
        acc+=frac;
        if(dash<=0)return null; // a 0-length dash still paints a dot with round linecap, so skip entirely
        return <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={s.color} strokeWidth={stroke} strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={offset} strokeLinecap="round"/>;
      })}
    </svg>
  );
}

function PodHealthTile({name,progress,status}){
  const sm=STATUS_META[status];
  return(
    <div style={{flex:"1 1 140px",minWidth:130,background:sm.bg,border:`1px solid ${sm.c}30`,borderRadius:16,padding:"16px 14px",textAlign:"center"}}>
      <div style={{fontSize:11,fontWeight:700,color:sm.c,textTransform:"uppercase",letterSpacing:0.6}}>{name}</div>
      <div style={{fontFamily:FONT_DISP,fontSize:28,fontWeight:700,color:sm.c,marginTop:6,lineHeight:1}}>{pct(progress)}</div>
      <div style={{fontSize:11,color:sm.c,marginTop:6,fontWeight:600}}>{sm.label}</div>
    </div>
  );
}

function SkeletonBox({width="100%",height=16,radius=8,style}){
  return <div className="skel" style={{width,height,borderRadius:radius,...style}}/>;
}

function OverviewSkeleton(){
  return(
    <div>
      {/* hero */}
      <div style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:20,padding:"30px 24px",marginBottom:16,textAlign:"center"}}>
        <div style={{display:"flex",justifyContent:"center"}}><SkeletonBox width={220} height={12} radius={6}/></div>
        <div style={{display:"flex",justifyContent:"center",margin:"16px 0 12px"}}>
          <SkeletonBox width={156} height={156} radius="50%"/>
        </div>
        <div style={{display:"flex",justifyContent:"center",marginBottom:14}}><SkeletonBox width={90} height={22} radius={99}/></div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
          <SkeletonBox width={480} height={12}/>
          <SkeletonBox width={340} height={12}/>
        </div>
      </div>

      {/* stat strip */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16,justifyContent:"center"}}>
        {[0,1,2].map(i=>(
          <div key={i} style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:14,padding:"12px 16px 12px 18px",flex:"1 1 150px",minWidth:140}}>
            <SkeletonBox width={70} height={9} radius={4} style={{marginBottom:10}}/>
            <SkeletonBox width={90} height={18} radius={6} style={{marginBottom:8}}/>
            <SkeletonBox width={60} height={10} radius={4}/>
          </div>
        ))}
      </div>

      {/* pod health */}
      <div style={{marginBottom:16}}>
        <SkeletonBox width={160} height={11} radius={4} style={{marginBottom:10}}/>
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          {[0,1,2,3,4].map(i=>(
            <div key={i} style={{flex:"1 1 140px",minWidth:130,background:C.linenDeep,border:`1px solid ${C.line}`,borderRadius:16,padding:"16px 14px",textAlign:"center"}}>
              <SkeletonBox width={60} height={9} radius={4} style={{margin:"0 auto 12px"}}/>
              <SkeletonBox width={50} height={24} radius={6} style={{margin:"0 auto 10px"}}/>
              <SkeletonBox width={70} height={9} radius={4} style={{margin:"0 auto"}}/>
            </div>
          ))}
        </div>
      </div>

      {/* trend + mix */}
      <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:16,alignItems:"stretch"}}>
        <div style={{flex:"2 1 380px",background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"18px 20px",minWidth:300}}>
          <SkeletonBox width={140} height={11} radius={4} style={{marginBottom:16}}/>
          <SkeletonBox width="100%" height={200} radius={10}/>
        </div>
        <div style={{flex:"1 1 260px",background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"18px 20px",minWidth:240,display:"flex",flexDirection:"column",alignItems:"center"}}>
          <SkeletonBox width={150} height={11} radius={4} style={{marginBottom:16,alignSelf:"flex-start"}}/>
          <SkeletonBox width={140} height={140} radius="50%"/>
          <div style={{display:"flex",flexDirection:"column",gap:9,marginTop:16,width:"100%"}}>
            {[0,1,2,3,4].map(i=>(<SkeletonBox key={i} width="100%" height={12} radius={4}/>))}
          </div>
        </div>
      </div>

      {/* spotlights */}
      <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
        {[0,1].map(i=>(
          <div key={i} style={{flex:"1 1 320px",minWidth:280,background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"18px 20px",display:"flex",alignItems:"center",gap:16}}>
            <SkeletonBox width={52} height={52} radius={14} style={{flexShrink:0}}/>
            <div style={{flex:1,minWidth:0}}>
              <SkeletonBox width={90} height={9} radius={4} style={{marginBottom:8}}/>
              <SkeletonBox width={140} height={14} radius={6} style={{marginBottom:8}}/>
              <SkeletonBox width={200} height={10} radius={4}/>
            </div>
            <SkeletonBox width={70} height={22} radius={99} style={{flexShrink:0}}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpotlightCard({label,agent,tone}){
  if(!agent)return null;
  const sm=STATUS_META[agent.status] || { label: "Unassigned", c: C.gray, bg: C.graySoft};
  const toneColor=tone==="good"?C.green:C.red;
  return(
    <div style={{flex:"1 1 320px",minWidth:280,background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"18px 20px",display:"flex",alignItems:"center",gap:16}}>
      <div style={{width:52,height:52,borderRadius:14,background:sm.bg,color:sm.c,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:18,flexShrink:0}}>{initials(agent.name)}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,fontWeight:700,color:toneColor,textTransform:"uppercase",letterSpacing:0.6}}>{label}</div>
        <div style={{fontFamily:FONT_DISP,fontWeight:700,fontSize:16,marginTop:2}}>{agent.name}</div>
        <div style={{fontSize:12.5,color:C.ink50,marginTop:2}}>Pod {agent.team} · {money(agent.net)} net · {pct(agent.progress)} of target</div>
      </div>
      <Pill label={sm.label} color={sm.c} bg={sm.bg}/>
    </div>
  );
}

function OverviewView({rows,teams,period, overviewData={}}){
  // const company=useMemo(()=>{
  //   const s={target:0,gross:0,net:0,refunds:0};
  //   rows.forEach(r=>{s.target+=r.target;s.gross+=r.gross;s.net+=r.net;s.refunds+=r.refunds;});
  //   s.progress=s.target?s.net/s.target:0;s.refundRatio=s.gross?s.refunds/s.gross:0;
  //   return s;
  // },[rows]);

  const company = overviewData.company || {}

  const csm=STATUS_META[statusOf(company.progress)];

  const podsSorted=[...teams].sort((a,b)=>b.progress-a.progress);
  const leadPod=podsSorted[0],lagPod=podsSorted[podsSorted.length-1];

  const starAgent=[...rows].sort((a,b)=>b.progress-a.progress)[0];
  const riskPool=[...rows].filter(r=>r.status==="behind"||r.status==="at_risk").sort((a,b)=>a.progress-b.progress);
  const focusAgent=riskPool[0]||[...rows].sort((a,b)=>a.progress-b.progress)[0];

  const trend=overviewData.trend||[];
  const trendCategories=useMemo(()=>trend.map(t=>t.label),[trend]);
  const trendSeries=useMemo(()=>[{label:"Net revenue",color:C.indigo,values:trend.map(t=>t.net)}],[trend]);

  const productMix=useMemo(()=>(overviewData.productMix?.slice(0, 5) ||[]).map((p,i)=>({...p,color:PRODUCT_PALETTE[i%PRODUCT_PALETTE.length]})),[overviewData.productMix]);
  const totalProductNet=productMix.reduce((s,p)=>s+Math.max(0,p.net),0)||1;
  const donutSlices=productMix.map(p=>({label:p.name,value:Math.max(0,p.net),color:p.color}));

  const summary=overviewData?.leadUnit && overviewData?.lagUnit ?`Company is tracking at ${pct(company.progress)} of target this ${period.label.toLowerCase()}. ${overviewData?.leadUnit?.name} leads at ${pct(overviewData?.leadUnit?.progress)}, while ${overviewData?.lagUnit?.name} trails at ${pct(overviewData?.lagUnit?.progress)}.`:`Company is tracking at ${pct(company.progress)} of target this ${period.label.toLowerCase()}.`;

  return(
    <div>
      {/* hero */}
      <div style={{background:C.card,border:`1px solid ${C.line}`,borderRadius:20,padding:"30px 24px",marginBottom:16,textAlign:"center"}}>
        <div style={{fontSize:12.5,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:1}}>Company performance · {period.label}</div>
        <div style={{display:"flex",justifyContent:"center",margin:"16px 0 12px"}}>
          <ProgressRing value={company.progress} color={csm.c} size={156} stroke={14}/>
        </div>
        <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>
          <Pill label={csm.label} color={csm.c} bg={csm.bg}/>
        </div>
        <div style={{maxWidth:580,margin:"0 auto",fontSize:14.5,color:C.ink70,lineHeight:1.55}}>{summary}</div>
      </div>

      {/* simple stat strip */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16,justifyContent:"center"}}>
        <StatTile label="Target" value={money(company.target)} sub={period.label} accent={C.ink70}/>
        <StatTile label="Net achieved" value={money(company.net)} sub={pct(company.progress)+" of target"} accent={C.indigo}/>
        <StatTile label="Lost to refunds" value={money(company.refunds)} sub={pct(company.refundRatio)+" of gross"} accent={C.red}/>
      </div>

      {/* pod health */}
      <div style={{marginBottom:16}}>
        <div style={{fontSize:12,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.6,marginBottom:10}}>Unit health at a glance</div>
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          {overviewData?.unitHealth?.map(t=>(<PodHealthTile key={t.name} name={t.name} progress={t.progress} status={t.status}/>))}
        </div>
      </div>

      {/* trend + mix */}
      <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:16,alignItems:"stretch"}}>
        <div style={{flex:"2 1 380px",background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"18px 20px",minWidth:300}}>
          <div style={{fontSize:12,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.6,marginBottom:12}}>Net revenue trend</div>
          <TrendChart categories={trendCategories} series={trendSeries} yFormatter={money} height={200}/>
        </div>
        <div style={{flex:"1 1 260px",background:C.card,border:`1px solid ${C.line}`,borderRadius:16,padding:"18px 20px",minWidth:240,display:"flex",flexDirection:"column",alignItems:"center"}}>
          <div style={{fontSize:12,color:C.ink50,fontWeight:600,textTransform:"uppercase",letterSpacing:0.6,marginBottom:14,alignSelf:"flex-start"}}>Revenue mix by product</div>
          <DonutChart slices={donutSlices} size={140}/>
          <div style={{display:"flex",flexDirection:"column",gap:7,marginTop:16,width:"100%"}}>
            {productMix.map(p=>(
              <div key={p.name} style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
                <span style={{width:8,height:8,borderRadius:99,background:p.color,flexShrink:0}}/>
                <span style={{flex:1,color:C.ink70,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</span>
                <span style={{color:C.ink50,fontFamily:FONT_MONO,fontSize:11.5}}>{pct(Math.max(0,p.net)/totalProductNet)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* spotlights */}
      <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
        <SpotlightCard label="Star performer" agent={overviewData?.starAgent || {}} tone="good"/>
        <SpotlightCard label="Needs a check-in" agent={overviewData?.focusAgent || {}} tone="warn"/>
      </div>
    </div>
  );
}

// ==========================================================================
export default function OverviewPage(){
  const apiCall = useApiCall();
  const [periodKey,setPeriodKey]=useState("this_month");
  const [pickerOpen,setPickerOpen]=useState(false);
  const period=PERIODS.find(p=>p.key===periodKey);
  const [overview, setOverview] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const rows=useMemo(()=>AGENTS.map(a=>{const r=computeRow(a,period);r.status=statusOf(r.progress);r.trend=trendOf(a.history);return r;}),[periodKey]);

  const teams=useMemo(()=>{
    const map={};
    TEAMS.forEach(t=>map[t]={name:t,agents:[],target:0,gross:0,net:0,refunds:0,onTarget:0,histSum:[0,0,0,0,0,0]});
    rows.forEach(r=>{
      const t=map[r.team];t.agents.push(r);
      t.target+=r.target;t.gross+=r.gross;t.net+=r.net;t.refunds+=r.refunds;
      if(r.status==="on_target")t.onTarget++;
      r.history.forEach((v,i)=>t.histSum[i]+=v);
    });
    return Object.values(map).map(t=>{
      const n=t.agents.length;
      const history=t.histSum.map(v=>v/n);
      const progress=t.target?t.net/t.target:0;
      const refundRatio=t.gross?t.refunds/t.gross:0;
      return {...t,count:n,history,progress,refundRatio,status:statusOf(progress),trend:trendOf(history)};
    });
  },[rows]);

  const getOverview = async() => {
    setIsLoading(true);
    try {
      const apiRes = await apiCall({url: `management/birds-eye-view?period=${periodKey}`})
      // console.log("apiRes", apiRes)
      setOverview(apiRes.data);
    } catch (error) {
      console.log("Error", error)
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getOverview();
  }, [periodKey])

  const selBar={padding:"9px 12px",borderRadius:10,border:`1px solid ${C.line}`,background:C.card,fontFamily:FONT_BODY,fontSize:13.5,color:C.ink,outline:"none",cursor:"pointer"};

  return(
    <div style={{minHeight:"100vh",background:C.linen,fontFamily:FONT_BODY,color:C.ink,padding:"24px 28px 40px"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box}table{border-collapse:collapse}
        ::-webkit-scrollbar{height:9px;width:9px}::-webkit-scrollbar-thumb{background:#D8C7AF;border-radius:9px}
        @keyframes skeletonPulse{0%,100%{opacity:.55}50%{opacity:1}}
        .skel{background:${C.lineSoft};animation:skeletonPulse 1.4s ease-in-out infinite}
      `}</style>

      <div style={{maxWidth:1240,margin:"0 auto"}}>
        {/* header */}
        <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",flexWrap:"wrap",gap:14,marginBottom:18}}>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:C.indigo,textTransform:"uppercase",letterSpacing:1.4}}>Legendesk · Sales</div>
            <h1 style={{fontFamily:FONT_DISP,fontSize:30,fontWeight:700,margin:"4px 0 0",letterSpacing:-0.5}}>Overview</h1>
          </div>
          <div style={{position:"relative"}}>
            <button onClick={()=>setPickerOpen(o=>!o)} style={{...selBar,display:"inline-flex",alignItems:"center",gap:10,fontWeight:600}}>
              <span style={{width:7,height:7,borderRadius:99,background:C.indigo}}/>{period.label}<span style={{color:C.ink30,fontSize:11}}>▼</span>
            </button>
            {pickerOpen&&(
              <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:C.card,border:`1px solid ${C.line}`,borderRadius:12,padding:6,boxShadow:"0 12px 30px rgba(10,12,18,.12)",zIndex:20,width:190}}>
                {PERIODS.map(p=>(<button key={p.key} onClick={()=>{setPeriodKey(p.key);setPickerOpen(false);}} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:FONT_BODY,fontSize:13.5,fontWeight:p.key===periodKey?600:400,color:p.key===periodKey?C.indigo:C.ink70,background:p.key===periodKey?C.indigoSoft:"transparent"}}>{p.label}</button>))}
              </div>
            )}
          </div>
        </div>

        {isLoading ? <OverviewSkeleton/> : <OverviewView rows={rows} teams={teams} period={period} overviewData={overview} />}
      </div>
    </div>
  );
}
